import {
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue';

import {
  normalizeAnchorTarget,
  scrollToAnchor,
  type AnchorTarget,
} from '@/composables/anchor-targets';

const ACTIVE_RATIO_THRESHOLD = 0.5;
const OBSERVER_THRESHOLDS = [0, ACTIVE_RATIO_THRESHOLD, 1] as const;

export interface UseActiveAnchorResult {
  readonly activeId: Ref<string>;
}

interface ObserverContext {
  readonly ids: readonly string[];
  readonly ratios: Map<string, number>;
  readonly activeId: Ref<string>;
  readonly isCurrent: () => boolean;
}

interface RatioUpdateContext {
  readonly ids: readonly string[];
  readonly ratios: Map<string, number>;
  readonly activeId: Ref<string>;
}

/**
 * 用 IntersectionObserver 跟踪当前可视 Section。sectionIds 必须按 HomePage
 * 实际 DOM 渲染顺序传入，两个 Section 同时达到阈值时用该顺序做稳定兜底。
 */
export function useActiveAnchor(
  sectionIds: MaybeRefOrGetter<readonly string[]>,
): UseActiveAnchorResult {
  const activeId = ref('');
  const ratios = new Map<string, number>();
  let observer: IntersectionObserver | null = null;
  let generation = 0;
  let disposed = false;

  const normalizedIds = computed(() => [...toValue(sectionIds)]);

  async function observeCurrentSections(
    ids: readonly string[],
    currentGeneration: number,
  ): Promise<void> {
    disconnectObserver(observer);
    observer = null;
    ratios.clear();
    activeId.value = '';
    await nextTick();
    if (disposed || currentGeneration !== generation) {
      return;
    }
    observer = createObserver({
      ids,
      ratios,
      activeId,
      isCurrent: isCurrentGeneration(currentGeneration),
    });
  }

  watch(normalizedIds, (ids, _previousIds, onCleanup) => {
    generation += 1;
    const currentGeneration = generation;
    onCleanup(() => {
      if (currentGeneration === generation) {
        generation += 1;
      }
    });
    void observeCurrentSections(ids, currentGeneration);
  }, { immediate: true });

  onBeforeUnmount(() => {
    disposed = true;
    generation += 1;
    disconnectObserver(observer);
    observer = null;
  });

  function isCurrentGeneration(currentGeneration: number): () => boolean {
    return () => !disposed && currentGeneration === generation;
  }

  return { activeId };
}

/** 处理 `/#section-id` 首次进入。只滚动一次，避免 revalidate 后反复拉回。 */
export function useInitialHashAnchorScroll(
  sectionIds: MaybeRefOrGetter<readonly string[]>,
): void {
  let hasTried = false;
  const normalizedIds = computed(() => [...toValue(sectionIds)]);
  watch(normalizedIds, async (ids) => {
    if (hasTried || ids.length === 0) {
      return;
    }
    hasTried = true;
    await nextTick();
    scrollToInitialHash(ids);
  }, { immediate: true });
}

function createObserver(context: ObserverContext): IntersectionObserver | null {
  if (context.ids.length === 0) {
    return null;
  }
  if (globalThis.IntersectionObserver === undefined) {
    // eslint-disable-next-line no-console
    console.warn('[useActiveAnchor] IntersectionObserver is not available');
    return null;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      if (!context.isCurrent()) {
        return;
      }
      updateRatios(entries, {
        ids: context.ids,
        ratios: context.ratios,
        activeId: context.activeId,
      });
    },
    { threshold: [...OBSERVER_THRESHOLDS] },
  );
  observeElements(observer, context.ids);
  return observer;
}

function observeElements(observer: IntersectionObserver, ids: readonly string[]): void {
  for (const id of ids) {
    const element = document.getElementById(id);
    if (element !== null) {
      observer.observe(element);
    }
  }
}

function updateRatios(
  entries: readonly IntersectionObserverEntry[],
  context: RatioUpdateContext,
): void {
  for (const entry of entries) {
    const id = entry.target.id;
    context.ratios.set(id, entry.isIntersecting ? entry.intersectionRatio : 0);
  }
  context.activeId.value = resolveActiveId(context.ids, context.ratios);
}

function resolveActiveId(ids: readonly string[], ratios: ReadonlyMap<string, number>): string {
  let bestId = '';
  let bestRatio = ACTIVE_RATIO_THRESHOLD;
  for (const id of ids) {
    const ratio = ratios.get(id) ?? 0;
    if (ratio >= bestRatio) {
      bestId = ratio > bestRatio || bestId === '' ? id : bestId;
      bestRatio = Math.max(bestRatio, ratio);
    }
  }
  return bestId;
}

function scrollToInitialHash(ids: readonly string[]): void {
  const target = normalizeLocationHash();
  if (target === null) {
    return;
  }
  if (!ids.includes(target.id)) {
    // eslint-disable-next-line no-console
    console.warn(`[AnchorNav] initial hash does not match a home section: "${target.hash}"`);
    return;
  }
  scrollToAnchor(target, { updateHash: false });
}

function normalizeLocationHash(): AnchorTarget | null {
  const hash = globalThis.location?.hash ?? '';
  return hash === '' ? null : normalizeAnchorTarget(hash);
}

function disconnectObserver(observer: IntersectionObserver | null): void {
  observer?.disconnect();
}
